import { useEffect, useState, useCallback } from "react";
import { Search, MapPin, Building2, ExternalLink, Sparkles, Wifi } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, Overline } from "../components/Reveal";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const JobCard = ({ job, delay }) => (
  <Reveal delay={delay}>
    <a
      data-testid={`job-card-${job.id}`}
      href={job.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-3xl bg-white p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(26,29,26,0.25)]"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-serif text-xl md:text-2xl tracking-tight text-ink leading-snug">{job.title}</h3>
        <ExternalLink size={16} strokeWidth={1.5} className="shrink-0 mt-1.5 text-smoke group-hover:text-sagedark transition-colors duration-300" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-smoke">
        <span className="inline-flex items-center gap-1.5"><Building2 size={14} strokeWidth={1.5} />{job.company}</span>
        <span className="inline-flex items-center gap-1.5"><MapPin size={14} strokeWidth={1.5} />{job.location}</span>
        {job.remote && <span className="inline-flex items-center gap-1.5 text-sagedark"><Wifi size={14} strokeWidth={1.5} />Remote</span>}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <span className="rounded-full bg-bone px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-smoke">{job.source}</span>
        {job.tags.slice(0, 2).map((t) => (
          <span key={t} className="rounded-full bg-sage/10 px-3 py-1 text-[11px] text-sagedark">{t}</span>
        ))}
      </div>
    </a>
  </Reveal>
);

export default function Offres() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [jobs, setJobs] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs", { params: q ? { query: q } : {} });
      setJobs(data.jobs);
      setCount(data.count);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/jobs/suggestions");
      setSuggestions(data.suggestions);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div data-testid="offres-page" className="bg-paper min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-[72px]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Reveal>
            <Overline>Le marché, fusionné en temps réel</Overline>
            <h1 className="mt-6 font-serif text-4xl md:text-6xl tracking-tight text-ink max-w-3xl leading-tight">
              Toutes les offres d'emploi, <em className="italic text-sagedark">en un seul lieu</em>.
            </h1>
            <p className="mt-6 max-w-xl text-sm md:text-base leading-relaxed text-smoke">
              Notre plateforme agrège et fusionne les principaux sites d'emploi européens en continu —
              dédoublonnées, actualisées, et enrichies par l'IA à partir de votre profil VAV.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-12">
            <div className="flex flex-col md:flex-row gap-4">
              <form
                data-testid="jobs-search-form"
                onSubmit={(e) => { e.preventDefault(); load(query); }}
                className="flex-1 flex items-center gap-3 bg-white border border-linen rounded-full px-6 py-2"
              >
                <Search size={18} strokeWidth={1.5} className="text-smoke shrink-0" />
                <input
                  data-testid="jobs-search-input"
                  className="flex-1 bg-transparent py-3 text-sm text-ink placeholder:text-smoke/60 focus:outline-none"
                  placeholder="Métier, entreprise, ville, compétence…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button data-testid="jobs-search-button" type="submit" className="rounded-full bg-ink text-paper text-sm px-6 py-2.5 hover:bg-sagedark transition-colors duration-300">
                  Rechercher
                </button>
              </form>
              {user && (
                <button
                  data-testid="jobs-ai-suggest-button"
                  onClick={suggest}
                  disabled={suggesting}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-sagedark text-paper text-sm px-6 py-3 hover:bg-sage disabled:opacity-50 transition-colors duration-300"
                >
                  <Sparkles size={16} strokeWidth={1.5} />
                  {suggesting ? "L'IA analyse votre profil…" : "Suggestions IA selon mon profil"}
                </button>
              )}
            </div>

            {suggestions && (
              <div data-testid="jobs-suggestions" className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-smoke mr-2">Pour vous :</span>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid={`suggestion-${s.slice(0, 20).replace(/\s+/g, "-")}`}
                    onClick={() => { setQuery(s); load(s); }}
                    className="rounded-full bg-white border border-linen px-4 py-2 text-sm text-ink hover:border-sage transition-colors duration-300"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </Reveal>

          <div className="mt-10 flex items-baseline gap-3">
            <p data-testid="jobs-count" className="font-serif text-3xl text-ink">{loading ? "…" : count}</p>
            <p className="text-sm text-smoke">offres fusionnées en temps réel (Arbeitnow, Remotive…)</p>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {loading ? (
              <p className="font-serif italic text-xl text-smoke md:col-span-2 py-20 text-center">Fusion des sources en cours…</p>
            ) : jobs.length === 0 ? (
              <p data-testid="jobs-empty" className="font-serif italic text-xl text-smoke md:col-span-2 py-20 text-center">Aucune offre pour cette recherche — essayez un autre mot-clé.</p>
            ) : (
              jobs.map((j, i) => <JobCard key={j.id} job={j} delay={0.02 * (i % 6)} />)
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
